import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Card,
} from 'antd';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import UserSearch from 'components/UserSearch';
import LocaleContext from 'locales';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { LinkButton } from 'components';
import { RequestInfo } from './components';
import FormItemApprovalLine from './components/FormItemApprovalLine';

const StyledTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  > .ant-tag {
    margin-right: 0px;
    max-width: 120px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    &:has(.anticon-close.ant-tag-close-icon) {
      position: relative;
      padding-right: 20px;
    }
    > .anticon-close.ant-tag-close-icon {
      position: absolute;
      right: 7px;
      top: 5px;
    }
  }
`;

function DriveCreate(props) {
  const [userSearchNamePath, setUserSearchNamePath] = useState(undefined); // UserSearch를 설정할 폼의 네임 패스
  const { locale } = useContext(LocaleContext);
  const { id, type, form, setIsWaitState, isCreate, detailData, onFinishDetail, backConf } = props;

  const [ownerList, setOwnerList] = useState([]);
  const [adminList, setAdminList] = useState([]);
  const [permissionList, setPermissionList] = useState([]);

  useEffect(() => {
    if (!id) {
      const applicant = localStorage.getItem('userId');
      form.setFieldsValue({
        applicant,
      });

      return;
    }
    if (detailData) {
      const contents = detailData?.contents;
      const { owners, admins, permissions } = contents[0];

      setOwnerList(owners);
      setAdminList(admins);
      setPermissionList(permissions);

      let approveDate = detailData?.approveDate;
      if (approveDate !== null && approveDate !== undefined) {
        approveDate = moment(approveDate);
      }

      // 사용자 결재인 경우
      if (detailData.approvalLines) {
        const newApprovalLines = detailData.approvalLines.map((el) => ({
          ...el,
          key: uuidv4(),
        }));
        detailData.approvalLines = newApprovalLines;
      }

      form.setFieldsValue({
        ...detailData,
        approveDate,
        contents: { ...contents[0], driveType: contents[0].driveType === 'share' ? 1 : 0 },
        applyDate: moment(detailData.applyDate).format('YYYY-MM-DD HH:mm:ss'),
      });

      if (setIsWaitState) {
        setIsWaitState(detailData.state === 'approval.state.waiting');
      }
    }
  }, [id, detailData]);

  // 사용자 선택되었을 때
  const onUserSearchSelected = (selectedUser) => {
    // 선택 취소면 아무것도 안한다.
    if (!selectedUser) {
      setUserSearchNamePath(undefined);
      return;
    }

    if (userSearchNamePath.some((path) => path === 'owners')) {
      const owners = ownerList.filter(
        (owner) => owner.sAMAccountName !== selectedUser.sAMAccountName,
      );
      setOwnerList([...owners, selectedUser]);

      const result = { [userSearchNamePath]: ownerList };
      form.setFieldsValue(result);
    }
    if (userSearchNamePath.some((path) => path === 'admins')) {
      const admins = adminList.filter(
        (admin) => admin.sAMAccountName !== selectedUser.sAMAccountName,
      );
      setAdminList([...admins, selectedUser]);

      const result = { [userSearchNamePath]: adminList };
      form.setFieldsValue(result);
    }
    if (userSearchNamePath.some((path) => path === 'permissions')) {
      if (
        !permissionList.some(
          (permission) => permission.sAMAccountName === selectedUser.sAMAccountName,
        )
      ) {
        const permissions = permissionList.filter(
          (permission) => permission.sAMAccountName !== selectedUser.sAMAccountName,
        );

        setPermissionList([...permissions, { ...selectedUser, permission: 'writable' }]);
      }

      const result = { [userSearchNamePath]: permissionList };
      form.setFieldsValue(result);
    }
    setUserSearchNamePath(undefined);
  };

  // 저장 시
  const onFinish = async (values) => {
    const payload = {
      ...values,
      type,
      user: values.applicant,
      contents: [
        {
          ...values.contents,
          driveType: values.contents.driveType === 1 ? 'share' : 'individual',
          owners: ownerList.map((owner) => ({
            sAMAccountName: owner.sAMAccountName,
            type: owner.type,
            name: owner.name,
            organization: owner.organization,
          })),
          admins: adminList.map((admin) => ({
            sAMAccountName: admin.sAMAccountName,
            type: admin.type,
            name: admin.name,
            organization: admin.organization,
          })),
          permissions: permissionList.map((user) => ({
            sAMAccountName: user.sAMAccountName,
            type: user.type,
            name: user.name,
            organization: user.organization,
            permission: user.permission,
            expireDate: user.expireDate || null,
          })),
        },
      ],
    };
    onFinishDetail(payload);
  };
  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 20 }}
      onFinish={onFinish}
    >
      <UserSearch visible={userSearchNamePath} onSelected={onUserSearchSelected} />
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <RequestInfo readOnly={!isCreate} />
          <Form.Item label={locale.resource['txt.approval-type']} name="type" value={type}>
            {locale.resource[type]}
          </Form.Item>
          <Form.Item
            label={locale.resource['network-drive.column.name']}
            name={['contents', 'driveName']}
            rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
          >
            {/* TODO 미리 정해진 네이밍 룰이 있는 것 같음 확인 필요 */}
            <Input disabled={!isCreate} />
          </Form.Item>
          <Form.Item
            label={locale.resource['txt.type']}
            name={['contents', 'driveType']}
            rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
            initialValue={1}
          >
            <Radio.Group disabled={!isCreate}>
              <Radio value={1} checked>
                {locale.resource['network-drive.tab.shared']}
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={`${locale.resource['network-drive.label.capacity']}(GB)`}
            name={['contents', 'driveSize']}
            rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
            initialValue={10}
          >
            <InputNumber addonAfter="GB" disabled={!isCreate} />
          </Form.Item>
          <Form.Item
            label={locale.resource['network-drive.form.owner']}
            name={['contents', 'owners']}
            required
            rules={[
              {
                validator: () => {
                  return ownerList.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(locale.resource['txt.required-field']));
                },
              },
            ]}
          >
            <Card
              extra={
                <LinkButton
                  disabled={!isCreate}
                  onClick={() => setUserSearchNamePath(['contents', 'owners'])}
                >
                  {locale.resource['txt.create']}
                </LinkButton>
              }
            >
              <StyledTags>
                {ownerList.map((owner) => (
                  <Tag
                    key={owner.sAMAccountName}
                    color="cyan"
                    closable={isCreate}
                    onClose={() => {
                      if (isCreate) {
                        setOwnerList(
                          ownerList.filter((o) => o.sAMAccountName !== owner.sAMAccountName),
                        );
                      }
                    }}
                  >
                    {owner.userName || owner.name}
                  </Tag>
                ))}
              </StyledTags>
            </Card>
          </Form.Item>
          <Form.Item
            label={locale.resource['txt.admin']}
            name={['contents', 'admins']}
            required
            rules={[
              {
                validator: () => {
                  return adminList.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(locale.resource['txt.required-field']));
                },
              },
            ]}
          >
            <Card
              extra={
                <LinkButton
                  disabled={!isCreate}
                  onClick={() => setUserSearchNamePath(['contents', 'admins'])}
                >
                  {locale.resource['txt.create']}
                </LinkButton>
              }
            >
              <StyledTags>
                {adminList.map((admin) => (
                  <Tag
                    key={admin.sAMAccountName}
                    color="orange"
                    style={{
                      maxWidth: 120,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    closable={isCreate}
                    onClose={() => {
                      if (isCreate) {
                        setAdminList(
                          adminList.filter((a) => a.sAMAccountName !== admin.sAMAccountName),
                        );
                      }
                    }}
                  >
                    {admin.userName || admin.name}
                  </Tag>
                ))}
              </StyledTags>
            </Card>
          </Form.Item>
          <Form.Item
            label={locale.resource['common.permission']}
            name={['contents', 'permissions']}
            required
            rules={[
              {
                validator: () => {
                  return permissionList.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(locale.resource['txt.required-field']));
                },
              },
            ]}
          >
            <Table
              size="small"
              pagination={false}
              rowKey="sAMAccountName"
              footer={
                isCreate
                  ? () => (
                      <Button
                        type="dashed"
                        block
                        onClick={() => setUserSearchNamePath(['contents', 'permissions'])}
                      >
                        {locale.resource['txt.create']}
                      </Button>
                    )
                  : null
              }
              columns={[
                {
                  title: locale.resource['network-drive.column.username.with.id'],
                  dataIndex: 'userName',
                  render: (value, record) =>
                    `${record.userName || record.name}(${record.sAMAccountName})`,
                },
                {
                  title: locale.resource['network-drive.column.department'],
                  dataIndex: 'organization',
                },
                {
                  title: locale.resource['network-drive.form.permission'],
                  dataIndex: 'permission',
                  width: 100,
                  render: (value, record, index) => (
                    <Select
                      defaultValue={value || 'writable'}
                      disabled={!isCreate}
                      options={[
                        {
                          label: locale.resource['permission.control.option.readWrite'],
                          value: 'writable',
                        },
                        {
                          label: locale.resource['permission.control.option.readOnly'],
                          value: 'readonly',
                        },
                        {
                          label: locale.resource['permission.control.option.invalid'],
                          value: 'invalid',
                        },
                      ]}
                      onChange={(v) => {
                        permissionList[index].permission = v;
                      }}
                    />
                  ),
                },
                {
                  title: locale.resource['network-drive.column.expire.date'],
                  dataIndex: 'expireDate',
                  width: 120,
                  render: (value, record, index) => (
                    <DatePicker
                      defaultValue={value && moment(value, 'YYYY-MM-DD')}
                      disabled={!isCreate}
                      onChange={(v) => {
                        permissionList[index].expireDate = v.format('YYYY-MM-DD');
                      }}
                    />
                  ),
                },
                {
                  title: locale.resource['txt.action'],
                  width: 110,
                  align: 'right',
                  render: (_, record) => (
                    <Space size="middle">
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        disabled={!isCreate}
                        onClick={() => {
                          const permissionData = permissionList.filter(
                            (permission) => permission.sAMAccountName !== record.sAMAccountName,
                          );
                          setPermissionList(permissionData);
                          form.setFieldsValue({ permissions: permissionData });
                        }}
                      >
                        {locale.resource['txt.delete']}
                      </Button>
                    </Space>
                  ),
                },
              ]}
              dataSource={permissionList}
            />
          </Form.Item>
          <FormItemApprovalLine isCreate={isCreate} form={form} backConf={backConf} type={type} />
        </Col>
      </Row>
    </Form>
  );
}

export default DriveCreate;
